import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { INotifyPayload } from '../interfaces/Appointment/INotifyPayload';

class SafeRetryPolicy implements signalR.IRetryPolicy {
  nextRetryDelayInMilliseconds(
    retryContext: signalR.RetryContext
  ): number | null {
    const attempt = retryContext.previousRetryCount;
    if (attempt > 100) return null; // just in case

    // Exponential backoff: 2^n * 1000ms up to 60s
    const delay = Math.min(60000, Math.pow(2, attempt) * 1000);

    // Add jitter (+/- up to 1s)
    const jitter = Math.random() * 2000 - 1000;

    return delay + jitter;
  }
}

@Injectable({ providedIn: 'root' })
export class AppointmentSignalService {
  private hubConnection!: signalR.HubConnection;
  private listeners: ((payload: INotifyPayload) => void)[] = [];

  public startConnection(tenantId: number) {
    if (this.hubConnection) return;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.NOTIFICICATION_HUB_URL}hub/appointments`, {
        withCredentials: false,
      })
      .withAutomaticReconnect(new SafeRetryPolicy())
      .configureLogging(signalR.LogLevel.None) // Remove to see logs
      .build();

    this.hubConnection
      .start()
      .then(() => {
        // console.log('SignalR connection started.');
        return this.hubConnection.invoke('JoinEhrTenantGroup', tenantId);
      })
      .then(() => {
        // console.log(`Joined ehr group tenant-${tenantId}-audience-ehr`)
      })
      .catch((err) => console.error('SignalR start error:', err));

    window.addEventListener('beforeunload', () => {
      this.stopConnection(tenantId);
    });

    this.hubConnection.on('AppointmentUpdated', (payload: INotifyPayload) => {
      // console.log('SignalR: AppointmentUpdated received:', payload);
      this.listeners.forEach((cb) => cb(payload));
    });
  }

  public onAppointmentUpdated(cb: (payload: INotifyPayload) => void) {
    this.listeners.push(cb);
  }

  public stopConnection(tenantId: number) {
    if (this.hubConnection) {
      this.hubConnection
        .invoke('LeaveEhrTenantGroup', `tenant-${tenantId}-audience-ehr`)
        .then(() => this.hubConnection.stop());
    }
  }
}
